/**
 * 模型族：下拉框里应该出现的**真实**选项。
 *
 * 为什么要有这一层：
 * 后端路由表里有 35 个 slug，但其中
 *   · 20 个是网关 / duomi 都没有的上游（Seedream、Sunburst、Topaz、Seedance、万相）
 *   · 6 个是同一个模型的别名（gpt-image-2 被拆成「官方 / 图生图 / 渠道 / 渠道编辑」）
 * 把它们原样渲染进下拉，用户看到的就是一屏假选项 + 一屏重复项。
 *
 * 所以前端只认「模型」，调用方式（文生图 / 图生图）由
 * **表单里有没有参考图**自动决定 —— 见 slugForFamily()。
 *
 * 清单以 `GET /api/aigc/models` 的 `families` 为唯一真源；
 * 下面这份是接口不可用时的兜底（离线 mock、后端没起、刚启动还没拉到），
 * 内容与后端 registry.FAMILIES 保持一致，改一处要同步另一处。
 *
 * ⚠️ 同步的是**过滤后**的 `registry.families()`，不是全量 `FAMILIES`。
 * 视频档的口径 2026-10-09 第二轮变了：**贰春只上架用户点名保留的 6 档**
 * （白名单真源在后端 `registry._YOUKOU_VIDEO_KEEP`，「可不可信」由 `verified` 标记
 * 区分），另加**非贰春**的 `google-omni`（速创）；非贰春其余档（网关 `minimax-h3`）
 * 仍按 `config.ONLY_VERIFIED_VIDEO` 只留已实测的。
 * 兜底清单必须跟着这条口径走 —— 它的意义是「后端连不上也能干活」，
 * 不是「偷偷把下游已隐藏/已下架的选项放回来」：上一版就发生过（3 档贰春模型上游已删，
 * 还挂在下拉里，点下去必然 `model_not_found`）。
 */

import type { ModelFamilyInfo } from '@/api/types'

/**
 * 图片档的能力 —— **与后端 `image_sizes.py` 逐项对齐**，也是兜底清单必须带
 * `capabilities` 的原因（视频档下面那段注释说的同一件事）。
 *
 * 2026-09-30 第二轮补：用户报「比例不够多」之后，详情页与批量页的
 * 「输出尺寸 / 比例」候选表的第二三段**就是从 `capabilities.aspect_ratios` 补的**。
 * 后端连不上时若这里是空的，候选会静默缩回平台那两三档 ——
 * 用户看到的是「比例又变少了」，而不是「后端没连上」。
 *
 * ⚠️ 这两份与后端是**同步**关系（改后端要改这里），不是第二真源：
 * 在线时的唯一真源仍然是 `GET /api/aigc/models`。
 */
const DUOMI_RATIOS = [
  '1:1', '4:5', '3:4', '2:3', '9:16', '1:2',
  '5:4', '4:3', '3:2', '16:9', '1.91:1', '2:1', '21:9',
]
/** songshuai 网关只认这 6 个（后端 `GATEWAY_IMAGE_RATIOS`）—— 有 4:5，没有 1:2 / 21:9 */
const GATEWAY_RATIOS = ['1:1', '4:5', '3:4', '2:3', '9:16', '16:9']

export const FALLBACK_FAMILIES: ModelFamilyInfo[] = [
  {
    id: 'gpt-image-2.5',
    name: 'GPT Image 2.5',
    kind: 'image',
    available: true,
    channel: 'duomi',
    channelLabel: 'duomi 直连',
    unitPrice: 0.1,
    unit: '张',
    textSlug: 'gpt-image-2-5-flare-text-to-image-economy',
    imageSlug: 'gpt-image-2-5-flare-image-to-image-economy',
    img2imgSameAsText: false,
    note: '实测约 25s 出图，日常首选',
    verified: true,
    reason: null,
    suggest: null,
    capabilities: { aspect_ratios: DUOMI_RATIOS },
  },
  {
    id: 'gpt-image-2',
    name: 'GPT Image 2',
    kind: 'image',
    available: true,
    channel: 'duomi',
    channelLabel: 'duomi 直连',
    unitPrice: 0.1,
    unit: '张',
    textSlug: 'gpt-image-2',
    imageSlug: 'gpt-image-2-image-to-image-official-stable',
    img2imgSameAsText: false,
    note: '细节更稳，但慢约 5 倍（约 130s）',
    verified: true,
    reason: null,
    suggest: null,
    capabilities: { aspect_ratios: DUOMI_RATIOS },
  },
  {
    id: 'nano-banana-2',
    name: 'Nano Banana 2',
    kind: 'image',
    available: true,
    channel: 'gateway',
    channelLabel: 'songshuai 网关',
    unitPrice: 0.2,
    unit: '张',
    textSlug: 'nano-banana2-gemini31flash-text-to-image-official-stable',
    imageSlug: 'nano-banana2-gemini31flash-image-to-image-official-stable',
    img2imgSameAsText: false,
    note: null,
    verified: false,
    reason: null,
    suggest: null,
    capabilities: { aspect_ratios: GATEWAY_RATIOS },
  },
  {
    id: 'nano-banana-pro',
    name: 'Nano Banana Pro',
    kind: 'image',
    available: true,
    channel: 'gateway',
    channelLabel: 'songshuai 网关',
    unitPrice: 0.3,
    unit: '张',
    textSlug: 'nano-banana-pro',
    imageSlug: 'nano-banana-pro',
    img2imgSameAsText: true,
    note: '同一模型兼顾文生图与图生图',
    verified: false,
    reason: null,
    suggest: null,
    capabilities: { aspect_ratios: GATEWAY_RATIOS },
  },
  {
    id: 'nano-banana-2-lite',
    name: 'Nano Banana 2 Lite',
    kind: 'image',
    available: true,
    channel: 'gateway',
    channelLabel: 'songshuai 网关',
    unitPrice: 0.1,
    unit: '张',
    textSlug: 'nano-banana-2-lite',
    imageSlug: 'nano-banana-2-lite',
    img2imgSameAsText: true,
    note: '最便宜，适合批量打样',
    verified: false,
    reason: null,
    suggest: null,
    capabilities: { aspect_ratios: GATEWAY_RATIOS },
  },
  /*
    视频档（2026-10-09 第二轮：**只列用户点名保留的 6 档 + Google Omni**）。

    · **6 档贰春模型** —— 用户给了**上游模型商城**的一张截图说「视频这块保留这几个
      就可以……其他不要先」。商城那 6 行是**分类**（一行常横跨我们这边 1–3 档），
      按名称 + 价格区间折算到本项目就是下面这 6 档；清单真源在后端
      `registry._YOUKOU_VIDEO_KEEP`，这里逐字对齐、顺序也与后端的下发顺序一致。
      「可不可信」仍由 `verified` 表达 —— 前端在下拉文本里挂「已实测 / 未实测」
      后缀（见 components/ModelSelect.tsx）。
      ⚠️ 隐藏 ≠ 下架：后端 `ROUTES` / `FAMILIES` 一个字没动，被隐藏的档只是
      **不上架**，老草稿 / `?model=` 深链照样解析得动（用户原话是「其他**先**不要」）。
    · **`google-omni`** —— 非贰春的那一条，走速创 API（2026-10-01 由网关切过来）。
      非贰春的其余视频档（网关 `minimax-h3`）仍按 `config.ONLY_VERIFIED_VIDEO`
      只留已实测的：它图生视频 0 成 2 败，本项目从没成功跑过，所以不在这里。

    ⚠️ 每条的 `capabilities` 都是**真值**（逐字对齐后端 `_YOUKOU_VIDEO_MODELS`），
    不是演示值：后端连不上时，视频页的清晰度 / 比例控件、参考图与参考视频上传框
    全靠它。回落全局清单会让「只有 480p/720p 的模型」显示出 480/720/1080 三个假档。

    ⚠️ 清晰度的**大小写必须原样**（seedance 系是小写 `480p`，渠道 / 阿里云系是
    大写 `480P`）—— 上游两种写法并存，抄错一个字母前端就会给出一个选不动的档。

    顺序 = 后端下发的下拉顺序：先 `google-omni`，再按计费方式分组
    （先按秒、后按次），组内按价格升序。
  */
  {
    id: 'google-omni',
    name: 'Google Omni',
    kind: 'video',
    available: true,
    channel: 'wuyin',
    channelLabel: '速创 API',
    unitPrice: 0.15,
    unit: '秒',
    textSlug: 'google-omni-text-to-video',
    imageSlug: 'google-omni-image-to-video',
    // ⚠️ false：这一档文生/图生是**两个 upstream slug**，不能像贰春那样合并
    img2imgSameAsText: false,
    note: '速创 API 直连 · 6/8/10 秒 · 参考图只取第 1 张',
    verified: true,
    reason: null,
    suggest: null,
    // 速创按「分辨率」而不是「清晰度档」出片，所以**不给 resolutions** ——
    // 给空数组的含义是「这个模型什么都不能选」（见 types.ts 那段注释），
    // 与「没说」是两回事。不给字段 = 回落全局默认，那才是实际的语义。
    capabilities: {
      aspect_ratios: ['16:9', '9:16'],
      ref_image_max: 1,
      ref_video_max: 0,
      /*
        2026-10-10 补 `duration_options`（与后端那两条路由同步，见
        `backend/app/registry.py` 的 `google-omni-*`）。

        这一档**只出 6 / 8 / 10 秒**（上游文档与两次实测都是这三个值），
        而它是页面新的默认引擎 —— 不把这三档如实告诉控件的话，
        时长会停在默认的 10 秒也没人拦，用户敲个 15 秒提交上去才发现上游不收。
        给了这一项，时长控件就只列这三枚（`allowCustom` 自动关掉，见 `DurationInput`）。

        ⚠️ 写错的代价不是编译错：写成 `[5, 8, 10]` 会让 5 秒那枚变成**必然失败**的按钮。
      */
      duration_options: [6, 8, 10],
    },
  },
  // ---- 贰春（youkou）：按秒计价的 4 档（时长直接影响价格，适合长片）----
  {
    id: 'youkou-wan-3-0',
    name: '阿里云 Wan 3.0',
    kind: 'video',
    available: true,
    channel: 'youkou',
    channelLabel: '贰春开放 API',
    unitPrice: 0.8,
    unit: '秒',
    textSlug: 'youkou-wan-3-0',
    imageSlug: 'youkou-wan-3-0',
    img2imgSameAsText: true,
    note: '¥0.158/0.4/0.8 秒（480P/720P/1080P）· 2-30s · 参考视频最多 5 条，本清单最高',
    verified: true,
    reason: null,
    suggest: null,
    capabilities: {
      resolutions: ['480P', '720P', '1080P'],
      aspect_ratios: ['adaptive', '16:9', '4:3', '1:1', '3:4', '9:16'],
      ref_image_max: 10,
      ref_video_max: 5,
    },
  },
  {
    id: 'youkou-sd25-mobile-deal',
    name: 'SD 2.5 移动渠道特价',
    kind: 'video',
    available: true,
    channel: 'youkou',
    channelLabel: '贰春开放 API',
    unitPrice: 0.84,
    unit: '秒',
    textSlug: 'youkou-sd25-mobile-deal',
    imageSlug: 'youkou-sd25-mobile-deal',
    img2imgSameAsText: true,
    note: '¥0.22/0.46/0.84 秒（480P/720P/1080P）· 4-30s · 支持 1080P',
    verified: false,
    reason: null,
    suggest: null,
    capabilities: {
      resolutions: ['480P', '720P', '1080P'],
      aspect_ratios: ['adaptive', '16:9', '4:3', '1:1', '3:4', '9:16'],
      ref_image_max: 10,
      ref_video_max: 3,
    },
  },
  {
    id: 'youkou-sd20-channel',
    name: 'SD 2.0 渠道特价',
    kind: 'video',
    available: true,
    channel: 'youkou',
    channelLabel: '贰春开放 API',
    unitPrice: 0.98,
    unit: '秒',
    textSlug: 'youkou-sd20-channel',
    imageSlug: 'youkou-sd20-channel',
    img2imgSameAsText: true,
    note: '¥0.18/0.42/0.98 秒（480P/720P/1080P）· 4-15s · 支持 1080P',
    verified: true,
    reason: null,
    suggest: null,
    capabilities: {
      resolutions: ['480P', '720P', '1080P'],
      aspect_ratios: ['adaptive', '16:9', '4:3', '1:1', '3:4', '9:16'],
      ref_image_max: 9,
      ref_video_max: 2,
    },
  },
  {
    id: 'youkou-seedance20-pro',
    name: 'Seedance 2.0 满血版',
    kind: 'video',
    available: true,
    channel: 'youkou',
    channelLabel: '贰春开放 API',
    unitPrice: 2.0,
    unit: '秒',
    textSlug: 'youkou-seedance20-pro',
    imageSlug: 'youkou-seedance20-pro',
    img2imgSameAsText: true,
    note: '¥0.38/0.73/2 秒（480p/720p/1080p）· 4-15s · Seedance 2.0 的高配档',
    verified: false,
    reason: null,
    suggest: null,
    capabilities: {
      resolutions: ['480p', '720p', '1080p'],
      aspect_ratios: ['16:9', '9:16', '1:1', '4:3', '3:4', '21:9'],
      ref_image_max: 9,
      ref_video_max: 3,
    },
  },
  // ---- 贰春（youkou）：按次计价的 2 档（时长不影响价格，适合批量铺量）----
  {
    id: 'youkou-sd20-chongliang',
    name: 'SD 2.0 冲量版',
    kind: 'video',
    available: true,
    channel: 'youkou',
    channelLabel: '贰春开放 API',
    unitPrice: 1.5,
    unit: '次',
    textSlug: 'youkou-sd20-chongliang',
    imageSlug: 'youkou-sd20-chongliang',
    img2imgSameAsText: true,
    note: '¥1.5/次 · 720P · 时长只有 5 / 10 / 15 三档可选',
    verified: false,
    reason: null,
    suggest: null,
    capabilities: {
      resolutions: ['720P'],
      aspect_ratios: ['9:16', '16:9', '1:1', '4:3', '21:9'],
      ref_image_max: 9,
      ref_video_max: 0,
    },
  },
  {
    id: 'youkou-sd25-chongliang',
    name: 'SD 2.5 冲量版',
    kind: 'video',
    available: true,
    channel: 'youkou',
    channelLabel: '贰春开放 API',
    unitPrice: 5.0,
    unit: '次',
    textSlug: 'youkou-sd25-chongliang',
    imageSlug: 'youkou-sd25-chongliang',
    img2imgSameAsText: true,
    note: '¥5/次 · 720P · 4-30s · 默认就是满 30 秒',
    verified: false,
    reason: null,
    suggest: null,
    capabilities: {
      resolutions: ['720P'],
      aspect_ratios: ['9:16', '16:9', '1:1', '4:3', '21:9'],
      ref_image_max: 10,
      ref_video_max: 0,
    },
  },
  // ---- 文本（LLM）----
  // 文案页的模型下拉也吃同一份清单。后端 registry.FAMILIES 里这两个是唯一
  // 真实可用的文案模型，别在前端另写一份。
  {
    id: 'deepseek-flash',
    name: 'DeepSeek V4.1 Flash',
    kind: 'text',
    available: true,
    channel: 'llm',
    channelLabel: 'DeepSeek 官方',
    unitPrice: 0.1,
    unit: '条',
    textSlug: 'deepseek-flash',
    imageSlug: null,
    img2imgSameAsText: false,
    note: '推理模型 · 文案性价比首选',
    verified: true,
    reason: null,
    suggest: null,
  },
  {
    id: 'deepseek-v4-pro',
    name: 'DeepSeek V4 Pro',
    kind: 'text',
    available: true,
    channel: 'llm',
    channelLabel: 'DeepSeek 官方',
    unitPrice: 0.2,
    unit: '条',
    textSlug: 'deepseek-v4-pro',
    imageSlug: null,
    img2imgSameAsText: false,
    note: '更强推理 · 长文案与复杂卖点',
    verified: true,
    reason: null,
    suggest: null,
  },
  // ---- 音频（语音合成，2026-10-08）----
  //
  // **它不出现在任何模型下拉里** —— `/tts` 页面选的是「音色」，不是模型
  // （音色清单来自 `GET /api/aigc/voices`，与数字人页同一份）。
  // 这条存在的唯一理由是：`JobCenterContext.submit()` 会拿表单里的 family id
  // 去 `familyById()` 折成真实路由 slug。清单里没有它时 `familyById` 返回 null，
  // 那个 id 被**原样当 slug 发出去** —— 后端 `resolve()` 按「未知 slug」降级到
  // 同 kind 的默认（恰好也是它），于是这次侥幸跑通。侥幸不该被依赖。
  //
  // `unit`/`unitPrice` 与后端 `pricing.PROVIDER_PRICE["minimax-tts"]` 一致（¥0.05/条）。
  //
  // ⚠️ `channel` 必须跟着**当前默认引擎**走（2026-10-08：`minimax-direct`，
  // 合成为官方直连）。它对应后端 `registry._tts(..., "minimax-tts", ...)`
  // → `_channel_of("minimax-tts")` → `minimax`。
  // 这条曾经写的是 `apilio`（旧默认引擎那一档）。抄错的代价不是编译错，
  // 而是**界面把「余额不足该去哪充值」指到另一家公司**。
  {
    id: 'tts',
    name: '语音合成',
    kind: 'audio',
    available: true,
    channel: 'minimax',
    channelLabel: 'MiniMax 官方',
    unitPrice: 0.05,
    unit: '条',
    textSlug: 'tts',
    imageSlug: null,
    img2imgSameAsText: true,
    note: '口播稿 → mp3 · 音色在页面里选',
    verified: true,
    reason: null,
    suggest: null,
  },
]

/** 默认模型：图片首选 GPT Image 2.5（duomi 直连，实测最快且不依赖网关余额）。 */
export const DEFAULT_FAMILY_ID: Record<'image' | 'video' | 'audio' | 'text', string> = {
  image: 'gpt-image-2.5',
  /*
    视频首选**Google Omni**（速创直连，¥0.15/秒，`verified: true` 实测出过片）。

    ## 2026-10-10：为什么从「阿里云 Wan 3.0」换到它

    用户口径是「一进页面右栏报价就是最贵的组合，第一次试流程的人一按就是 12 块，
    这不符合人性」。查下来是两个默认值叠出来的：
    `DEFAULT_VIDEO_RESOLUTION` 取的是 1080P（三档里最贵的一档），
    加上 Wan 3.0 的 1080P 档 ¥0.8/秒 × 默认 15 秒 = **¥12.00**。

    现在这一整套默认值换成「先跑通、再升规格」：
    Google Omni ¥0.15/秒 × 10 秒 = **¥1.50**（便宜到 1/8）。
    它也是下拉里的第一条（后端按「先速创、再按单价升序」下发）——
    默认值与「用户闭着眼选第一条」拿到的是同一档，不会再出现
    「默认那条比清单里最便宜的贵 5 倍」这种事。

    ## 它的能力边界由模板那边兜住

    它只出 **6/8/10 秒、只有 16:9 与 9:16**，所以 15 秒与 1:1 的模板
    **各自带了 `recommendedModel`**（`youkou-wan-3-0`，清单里最便宜的「全能力」档），
    点那条模板时会把引擎一起换过去 —— 见 `VideoTemplate.recommendedModel`。
    没有那一层的话，「点 15 秒的模板 → 时长被夹回 10 秒 → 模板卡片脱钩」。
    这条不变量由 `smoke-video-templates.ts` 的第 ⑦ 条逐条核对，不靠人记。

    这里改过三次值：网关 `minimax-h3`（¥0.30/秒，从没跑通）→ `youkou-minimax-h3`
    （上游已下架）→ `youkou-wan-3-0`（本轮之前，能力最全但默认组合最贵）→ 本条。
    默认值必须始终指向清单里真实存在的一项 ——
    否则新任务的初始模型在下拉里根本选不出来，`ModelSelect` 会在挂载后立刻把它改掉。
  */
  video: 'google-omni',
  text: 'deepseek-flash',
  /*
    音频只有一档（`tts`，默认由 MiniMax 官方直连合成，¥0.05/条）。**这个值不驱动任何下拉** ——
    `/tts` 页面上选的是音色，模型（`speech-2.8-hd`）由后端的配音引擎开关决定；
    它存在是为了让 `ModelsContext.normalize()` 有地方落，
    以及「哪天 audio 多一条」时默认值有明确出处。
  */
  audio: 'tts',
}

export const familiesOf = (list: ModelFamilyInfo[], kind: 'image' | 'video' | 'audio' | 'text') =>
  list.filter((f) => f.kind === kind && f.available)

export const familyById = (list: ModelFamilyInfo[], id: string) =>
  list.find((f) => f.id === id) ?? null

/**
 * 表单里存的旧值可能是 slug（升级前遗留的草稿），转成 family id。
 * 找不到就返回 null，由调用方兜底到默认模型。
 */
export const familyIdFromLegacy = (list: ModelFamilyInfo[], value: string) => {
  const direct = list.find((f) => f.id === value)
  if (direct) return direct.id
  const bySlug = list.find((f) => f.textSlug === value || f.imageSlug === value)
  return bySlug?.id ?? null
}

/**
 * 模型 + 「有没有参考图」→ 实际提交的 slug。
 *
 * 这是整套聚合的落点：用户选的是模型，不是调用方式。
 */
export const slugForFamily = (family: ModelFamilyInfo, hasRef: boolean): string => {
  if (hasRef && family.imageSlug) return family.imageSlug
  return family.textSlug ?? family.imageSlug ?? ''
}

/** 下拉里那一行显示什么：名字 + 关键差异，不堆参数。 */
export const familyUpsell = (f: ModelFamilyInfo): string => {
  const bits: string[] = []
  if (f.unitPrice) bits.push(`¥${f.unitPrice.toFixed(2)}/${f.unit}`)
  if (f.verified) bits.push('已实测')
  return bits.join(' · ')
}
